// ============================================================
// ProgressBar — Game progress with segment markers and key moments
// Shows position in game, segment boundaries, key moment dots
// ============================================================

import { useCallback, useRef, useMemo } from "react"
import type { PhaseNarration, MomentCallout, MoveData } from "@shared/types"
import styles from "./ProgressBar.module.css"

interface ProgressBarProps {
  currentMoveIndex: number
  totalMoves: number
  moves: MoveData[]
  phases: PhaseNarration[]
  momentCallouts: MomentCallout[]
  onSeek: (moveIndex: number) => void
}

function moveNumberToIndex(
  moves: MoveData[],
  moveNumber: number,
  color?: "white" | "black"
): number {
  // Find the exact half-move when color is provided, otherwise first match
  const idx = color
    ? moves.findIndex((m) => m.moveNumber === moveNumber && m.color === color)
    : moves.findIndex((m) => m.moveNumber === moveNumber)
  return idx === -1 ? 0 : idx
}

export const ProgressBar = ({
  currentMoveIndex,
  totalMoves,
  moves,
  phases,
  momentCallouts,
  onSeek,
}: ProgressBarProps) => {
  const barRef = useRef<HTMLDivElement>(null)

  // Calculate current position as percentage
  const progress =
    totalMoves > 0 ? Math.max(0, (currentMoveIndex + 1) / totalMoves) : 0

  // Calculate segment boundary positions
  const segmentBoundaries = phases.map((phase) => {
    const idx = moveNumberToIndex(moves, phase.moveStart)
    return idx / Math.max(totalMoves - 1, 1)
  })

  // Calculate key moment marker positions
  const keyMomentPositions = momentCallouts.map((callout) => {
    const idx = moveNumberToIndex(moves, callout.moveNumber, callout.color)
    return {
      position: idx / Math.max(totalMoves - 1, 1),
      moveIndex: idx,
      isCritical: callout.arrows.length > 1, // Multiple arrows = critical moment
      moveNumber: callout.moveNumber,
      text: callout.text,
    }
  })

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!barRef.current || totalMoves === 0) return
      const rect = barRef.current.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const percentage = clickX / rect.width
      const targetIndex = Math.round(percentage * (totalMoves - 1))
      onSeek(Math.max(-1, Math.min(targetIndex, totalMoves - 1)))
    },
    [totalMoves, onSeek]
  )

  // Drag support
  const isDragging = useRef(false)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      isDragging.current = true
      handleClick(e)

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current || !barRef.current) return
        const rect = barRef.current.getBoundingClientRect()
        const x = moveEvent.clientX - rect.left
        const percentage = Math.max(0, Math.min(1, x / rect.width))
        const targetIndex = Math.round(percentage * (totalMoves - 1))
        onSeek(Math.max(-1, Math.min(targetIndex, totalMoves - 1)))
      }

      const handleMouseUp = () => {
        isDragging.current = false
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    },
    [totalMoves, onSeek, handleClick]
  )

  // Touch drag support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!barRef.current || totalMoves === 0) return
      const touch = e.touches[0]
      if (!touch) return
      const rect = barRef.current.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const percentage = Math.max(0, Math.min(1, x / rect.width))
      const targetIndex = Math.round(percentage * (totalMoves - 1))
      onSeek(Math.max(-1, Math.min(targetIndex, totalMoves - 1)))
    },
    [totalMoves, onSeek]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!barRef.current || totalMoves === 0) return
      const touch = e.touches[0]
      if (!touch) return
      const rect = barRef.current.getBoundingClientRect()
      const x = touch.clientX - rect.left
      const percentage = Math.max(0, Math.min(1, x / rect.width))
      const targetIndex = Math.round(percentage * (totalMoves - 1))
      onSeek(Math.max(-1, Math.min(targetIndex, totalMoves - 1)))
    },
    [totalMoves, onSeek]
  )

  // Keyboard navigation for accessibility
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (totalMoves === 0) return
      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault()
          onSeek(Math.max(-1, currentMoveIndex - 1))
          break
        case "ArrowRight":
          e.preventDefault()
          onSeek(Math.min(totalMoves - 1, currentMoveIndex + 1))
          break
        case "Home":
          e.preventDefault()
          onSeek(-1)
          break
        case "End":
          e.preventDefault()
          onSeek(totalMoves - 1)
          break
      }
    },
    [totalMoves, currentMoveIndex, onSeek]
  )

  // Descriptive value text for screen readers
  const currentMove = moves[currentMoveIndex]
  const valueText = useMemo(() => {
    if (currentMoveIndex < 0) return "Starting position"
    if (!currentMove) return `Move ${currentMoveIndex + 1} of ${totalMoves}`
    return `Move ${currentMoveIndex + 1} of ${totalMoves}: ${currentMove.color} move ${currentMove.moveNumber}`
  }, [currentMoveIndex, totalMoves, currentMove])

  return (
    <div
      className={styles.container}
      ref={barRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onKeyDown={handleKeyDown}
      role="slider"
      aria-label="Game progress"
      aria-valuenow={currentMoveIndex + 1}
      aria-valuemin={0}
      aria-valuemax={totalMoves}
      aria-valuetext={valueText}
      tabIndex={0}
    >
      {/* Track */}
      <div className={styles.track}>
        {/* Filled portion */}
        <div
          className={styles.filled}
          style={{ width: `${progress * 100}%` }}
        />

        {/* Segment boundary markers */}
        {segmentBoundaries.map((pos, i) => (
          <div
            key={`seg-${i}`}
            className={styles.segmentMarker}
            style={{ left: `${pos * 100}%` }}
          />
        ))}

        {/* Key moment markers */}
        {keyMomentPositions.map((marker, i) => {
          const isReached = currentMoveIndex === marker.moveIndex
          return (
            <div
              key={`key-${i}`}
              className={`${styles.keyMomentMarker} ${
                marker.isCritical ? styles.criticalMarker : ""
              } ${isReached ? styles.keyMomentReached : ""}`}
              style={{ left: `${marker.position * 100}%` }}
              title={`Move ${marker.moveNumber}`}
            />
          )
        })}

        {/* Current position indicator */}
        <div
          className={styles.indicator}
          style={{ left: `${progress * 100}%` }}
        />
      </div>
    </div>
  )
}
